import { CheckCircleRounded, CircleRounded } from '@mui/icons-material';
import { Box, Card, CardActionArea, Grid, styled } from '@mui/material';
import { AppCard as App, type AppCardItem } from '~/components/AppCard';
import { FolderCard as Folder } from '~/components/FolderCard';
import { Tooltip } from '~/components/Tooltip';
import { ContentCardActions, ContentCardThumbnail } from '~/components/media/card';
import type {
  MediaSelect__ContentFolder as ContentFolder,
  MediaSelect__MediaItem as MediaItem,
} from '../index.generated';

export const SelectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  '&.disabled': {
    opacity: 0.3,
  },
  '&.selected': {
    border: `5px solid ${theme.palette.divider}`,
  },
  // The card clips its action area, so ring the card instead.
  '&:has(> .MuiCardActionArea-root.Mui-focusVisible)': theme.mixins.focusRing,
  '& > .MuiCardActionArea-root.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
}));

const SelectedIcon = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: '40px',
  justifyContent: 'center',
  position: 'absolute',
  width: '40px',
  padding: theme.spacing(1),
}));

export interface SelectedIndicatorProps {
  selected: boolean;
}

const SelectedIndicator = ({ selected }: SelectedIndicatorProps) => (
  <>
    <SelectedIcon sx={{ zIndex: 1 }}>
      <CircleRounded sx={{ color: 'white', fontSize: '1.4rem' }} />
    </SelectedIcon>
    {selected && (
      <SelectedIcon sx={{ zIndex: 2 }}>
        <CheckCircleRounded color="secondary" />
      </SelectedIcon>
    )}
  </>
);

export interface AppCardProps {
  app: AppCardItem & { __typename: 'ContentItem'; updatedAt: string };
  onClick: () => void;
  selected: boolean;
}

export const AppCard = ({ app, onClick, selected }: AppCardProps) => (
  <Grid item xs={12} sm={6} md={3} lg={2}>
    <SelectCard className={selected ? 'selected' : ''}>
      <CardActionArea aria-pressed={selected} onClick={onClick}>
        <SelectedIndicator selected={selected} />
        <App
          key={app.id}
          app={app}
          content={<ContentCardActions interactive={false} item={app} />}
        />
      </CardActionArea>
    </SelectCard>
  </Grid>
);

export interface FolderCardProps {
  folder: ContentFolder;
  onClick: () => void;
}

export const FolderCard = ({ folder, onClick }: FolderCardProps) => (
  <Grid item xs={12} sm={6} md={3} lg={2}>
    <SelectCard>
      <CardActionArea onClick={onClick}>
        <Folder folder={folder} />
      </CardActionArea>
    </SelectCard>
  </Grid>
);

export const FolderCardSmall = ({ folder, onClick }: FolderCardProps) => (
  <Grid item xs={6}>
    <SelectCard>
      <CardActionArea onClick={onClick}>
        <Folder folder={folder} />
      </CardActionArea>
    </SelectCard>
  </Grid>
);

export interface MediaItemCardProps {
  mediaItem: MediaItem;
  onClick: () => void;
  selected: boolean;
}

export const MediaItemCard = ({ mediaItem, onClick, selected }: MediaItemCardProps) => (
  <Grid item xs={12} sm={6} md={3} lg={2}>
    <SelectCard className={selected ? 'selected' : ''}>
      <CardActionArea aria-pressed={selected} onClick={onClick}>
        <SelectedIndicator selected={selected} />
        <ContentCardThumbnail item={mediaItem} />
        <ContentCardActions interactive={false} item={mediaItem} />
      </CardActionArea>
    </SelectCard>
  </Grid>
);

export const MediaItemCardSmall = ({
  mediaItem,
  onClick,
}: Omit<MediaItemCardProps, 'selected'>) => {
  const disabled = mediaItem.status !== 'READY';
  return (
    <Grid item xs={6}>
      <Tooltip title={disabled && 'This item is not ready for use yet.'}>
        <SelectCard className={disabled ? 'disabled' : ''}>
          <CardActionArea disabled={disabled} onClick={onClick}>
            <ContentCardThumbnail item={mediaItem} />
            <ContentCardActions interactive={false} item={mediaItem} small />
          </CardActionArea>
        </SelectCard>
      </Tooltip>
    </Grid>
  );
};
